import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, ArrowUpRight, Target, Cpu, Compass, BadgeCheck, Globe2, ShieldCheck } from 'lucide-react';
import Marquee from '@/components/Marquee';
import { MaskedLines, FadeUp, Overline, EASE } from '@/components/Reveal';
import { SEMINARS, INCLUDES, ENDORSEMENTS, MARQUEE_ITEMS, IMAGES } from '@/data/content';

const PILLARS = [
  { icon: Target, title: 'Autogestión', desc: 'Disciplina férrea para administrar su tiempo, sus estudios y sus metas sin depender de nadie.' },
  { icon: Cpu, title: 'Dominio tecnológico', desc: 'Inteligencia artificial, herramientas creativas y metodologías ágiles como ventaja real.' },
  { icon: Compass, title: 'Liderazgo estratégico', desc: 'La iniciativa para tomar el control, comunicar con autoridad y liderar equipos.' },
];

const FEATURED = ['06', '01', '08', '05'];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const imgY = useTransform(scrollYProgress, [0, 1], ['0%', '14%']);
  const ghostY = useTransform(scrollYProgress, [0, 1], ['0%', '40%']);

  return (
    <main>
      {/* ============ HERO ============ */}
      <section ref={heroRef} data-testid="hero-section" className="noise relative overflow-hidden bg-navy">
        <motion.span
          aria-hidden
          style={{ y: ghostY }}
          className="text-stroke-white pointer-events-none absolute -bottom-10 left-0 z-0 select-none whitespace-nowrap font-display text-[24vw] font-extrabold uppercase leading-none"
        >
          FUTURE
        </motion.span>

        <div className="relative z-10 mx-auto grid max-w-7xl items-center gap-14 px-4 pb-28 pt-32 sm:px-6 lg:grid-cols-12 lg:px-8 lg:pb-36 lg:pt-44">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: EASE, delay: 0.1 }}
            >
              <Overline className="text-gold">Programa Advanced · Jóvenes 14–18 años</Overline>
            </motion.div>

            <MaskedLines
              as="h1"
              delay={0.2}
              className="mt-7 font-display text-4xl font-extrabold uppercase leading-[1.02] tracking-tight sm:text-5xl lg:text-6xl"
              lines={[
                <>El nivel de</>,
                <>los <span className="text-crimson">líderes</span></>,
              ]}
            />

            <motion.p
              data-testid="hero-subtitle"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: EASE, delay: 0.55 }}
              className="mt-7 max-w-xl text-base leading-relaxed text-white/70 sm:text-lg"
            >
              Diseñado para jóvenes y familias que no buscan resultados “promedio”, sino expedientes
              absolutamente sobresalientes. Cerramos la brecha entre la educación tradicional y las
              exigencias del mundo real.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: EASE, delay: 0.7 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <Link to="/seminarios" data-testid="hero-cta-seminarios" className="btn-primary group">
                Explorar los 8 seminarios
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link to="/aval-internacional" data-testid="hero-cta-aval" className="btn-ghost">
                Aval internacional
              </Link>
            </motion.div>

            <motion.dl
              data-testid="hero-stats"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.9 }}
              className="mt-14 grid max-w-lg grid-cols-3 gap-6 border-t border-white/10 pt-8"
            >
              {[
                ['08', 'Seminarios tácticos'],
                ['03', 'Instituciones avalan'],
                ['01', 'Certificado internacional'],
              ].map(([n, label]) => (
                <div key={label}>
                  <dt className="font-display text-3xl font-extrabold text-gold sm:text-4xl">{n}</dt>
                  <dd className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">{label}</dd>
                </div>
              ))}
            </motion.dl>
          </div>

          <div className="relative lg:col-span-5">
            <motion.div
              initial={{ clipPath: 'inset(100% 0% 0% 0%)' }}
              animate={{ clipPath: 'inset(0% 0% 0% 0%)' }}
              transition={{ duration: 1.1, ease: EASE, delay: 0.4 }}
              className="relative rounded-t-[200px] border border-gold/30 p-3 sm:rounded-t-[240px]"
            >
              <div className="overflow-hidden rounded-t-[188px] sm:rounded-t-[228px]">
                <motion.img
                  src={IMAGES.hero}
                  alt="Jóvenes estudiantes caminando por el campus"
                  style={{ y: imgY }}
                  className="h-[420px] w-full scale-110 object-cover sm:h-[520px] lg:h-[560px] max-h-[80vh]"
                  data-testid="hero-image"
                />
              </div>
              <motion.div
                initial={{ opacity: 0, y: 24, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.7, ease: EASE, delay: 1.05 }}
                className="absolute -left-6 bottom-12 flex items-center gap-3 rounded-2xl border border-white/15 bg-navy/80 px-5 py-4 backdrop-blur-xl sm:-left-12"
                data-testid="hero-aval-badge"
              >
                <img src={IMAGES.flag} alt="Bandera de Estados Unidos" className="h-8 w-12 rounded-[3px] object-cover" />
                <div>
                  <p className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-gold">Avalado en EE.UU.</p>
                  <p className="mt-0.5 text-xs text-white/70">Florida Global University · Lic. #3180</p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============ MARQUEE ============ */}
      <div className="bg-crimson text-white">
        <Marquee items={MARQUEE_ITEMS} testId="home-marquee" />
      </div>

      {/* ============ ALIANZA ============ */}
      <section data-testid="alliance-section" className="relative overflow-hidden bg-porcelain py-16 text-navy sm:py-24 lg:py-32">
        <span aria-hidden className="text-stroke-navy pointer-events-none absolute -top-6 right-0 select-none font-display text-[18vw] font-extrabold uppercase leading-none">
          EE.UU.
        </span>
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-end gap-10 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <FadeUp>
                <Overline className="text-crimson">Respaldo y aval internacional</Overline>
              </FadeUp>
              <MaskedLines
                inView
                className="mt-6 font-display text-2xl font-extrabold uppercase leading-tight tracking-tight sm:text-3xl lg:text-4xl"
                lines={[<>Una triple alianza que</>, <>garantiza <span className="font-serif normal-case italic text-crimson">excelencia</span></>]}
              />
              <FadeUp delay={0.15}>
                <p className="mt-6 max-w-xl text-base leading-relaxed text-navy/70">
                  Las certificaciones de su hijo cuentan con el máximo estándar de excelencia académica
                  y tienen peso real en Estados Unidos y el mundo.
                </p>
              </FadeUp>
            </div>
            <FadeUp delay={0.2} className="lg:col-span-5">
              <div className="overflow-hidden rounded-2xl border-4 border-white shadow-2xl shadow-navy/20">
                <img src={IMAGES.flag} alt="Bandera de Estados Unidos ondeando" className="h-52 w-full object-cover sm:h-64" data-testid="alliance-flag-image" />
              </div>
            </FadeUp>
          </div>

          <div className="mt-16 grid gap-6 md:grid-cols-3 lg:gap-8">
            {ENDORSEMENTS.map((e, i) => (
              <FadeUp key={e.id} delay={0.1 + i * 0.12}>
                <article
                  data-testid={`alliance-card-${e.id}`}
                  className="group relative h-full rounded-2xl border border-navy/10 bg-white p-6 transition-all duration-500 hover:-translate-y-1.5 hover:border-crimson/40 hover:shadow-[0_24px_60px_-20px_rgba(217,27,92,0.35)] sm:p-8"
                >
                  <div className="flex items-start justify-between">
                    <span className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-crimson">{e.role}</span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-navy/40">{e.since}</span>
                  </div>
                  <h3 className="mt-5 font-display text-xl font-extrabold leading-tight tracking-tight sm:text-2xl">{e.name}</h3>
                  <p className="mt-4 text-sm leading-relaxed text-navy/60">{e.desc}</p>
                  {e.license && (
                    <span className="mt-5 inline-block rounded-full bg-gold/20 px-4 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-navy">
                      {e.license}
                    </span>
                  )}
                  <span className="text-stroke-navy pointer-events-none absolute -bottom-4 right-4 select-none font-display text-7xl font-extrabold transition-all duration-500 group-hover:[-webkit-text-stroke-color:rgba(217,27,92,0.5)]">
                    0{i + 1}
                  </span>
                </article>
              </FadeUp>
            ))}
          </div>

          <FadeUp delay={0.2}>
            <Link
              to="/aval-internacional"
              data-testid="alliance-cta"
              className="group mt-12 inline-flex items-center gap-3 font-mono text-xs font-bold uppercase tracking-[0.25em] text-crimson"
            >
              Conocer el aval completo
              <span className="flex h-10 w-10 items-center justify-center rounded-full border border-crimson/40 transition-all duration-300 group-hover:bg-crimson group-hover:text-white">
                <ArrowUpRight className="h-4 w-4" />
              </span>
            </Link>
          </FadeUp>
        </div>
      </section>

      {/* ============ MANIFIESTO ============ */}
      <section data-testid="manifesto-section" className="noise relative overflow-hidden bg-navy py-16 sm:py-24 lg:py-32">
        <div className="relative z-10 mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-6 lg:grid-cols-12 lg:px-8">
          <div className="lg:col-span-7">
            <FadeUp>
              <Overline className="text-gold">Manifiesto</Overline>
            </FadeUp>
            <MaskedLines
              inView
              className="mt-6 font-display text-2xl font-extrabold uppercase leading-tight tracking-tight sm:text-3xl lg:text-4xl"
              lines={[<>Cerramos la brecha entre la</>, <>educación tradicional y el</>, <><span className="text-crimson">mundo real</span></>]}
            />
            <div className="mt-12 space-y-0">
              {PILLARS.map((p, i) => (
                <FadeUp key={p.title} delay={0.1 + i * 0.1}>
                  <div className="group flex items-start gap-6 border-t border-white/10 py-7 transition-colors duration-500 last:border-b hover:bg-white/[0.03]">
                    <span className="font-display text-xl font-extrabold text-crimson">0{i + 1}</span>
                    <p.icon className="mt-1 h-6 w-6 shrink-0 text-gold" />
                    <div>
                      <h3 className="font-display text-xl font-bold tracking-tight sm:text-2xl">{p.title}</h3>
                      <p className="mt-2 max-w-md text-sm leading-relaxed text-white/55">{p.desc}</p>
                    </div>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
          <FadeUp delay={0.2} className="lg:col-span-5">
            <div className="relative">
              <img
                src={IMAGES.teensGroup}
                alt="Grupo de jóvenes estudiantes sonriendo"
                className="w-full mix-blend-screen"
                data-testid="manifesto-image"
              />
              <p className="mt-4 text-center font-serif text-xl italic text-white/60">
                Convivencia, equipo y ambición sana — de 14 a 18 años.
              </p>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ============ SEMINARIOS PREVIEW ============ */}
      <section data-testid="seminars-preview-section" className="bg-porcelain py-16 text-navy sm:py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-end justify-between gap-8">
            <div>
              <FadeUp>
                <Overline className="text-crimson">Estructura de alto rendimiento</Overline>
              </FadeUp>
              <MaskedLines
                inView
                className="mt-6 font-display text-2xl font-extrabold uppercase leading-tight tracking-tight sm:text-3xl lg:text-4xl"
                lines={[<>8 seminarios</>, <><span className="text-crimson">tácticos</span></>]}
              />
            </div>
            <FadeUp delay={0.15}>
              <p className="max-w-sm text-sm leading-relaxed text-navy/60">
                El programa Advanced lleva al estudiante a través de 8 seminarios tácticos que instalan
                las bases que las mejores universidades exigen.
              </p>
            </FadeUp>
          </div>

          <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-4 lg:grid-rows-2">
            {FEATURED.map((id, i) => {
              const s = SEMINARS.find((x) => x.id === id);
              return (
                <FadeUp key={id} delay={0.08 * i} className={i === 0 ? 'md:col-span-2 lg:row-span-2' : ''}>
                  <Link
                    to="/seminarios"
                    data-testid={`seminar-preview-card-${id}`}
                    className={`group flex h-full flex-col justify-between rounded-2xl p-6 transition-all duration-500 hover:-translate-y-1.5 sm:p-8 ${
                      i === 0
                        ? 'noise bg-crimson text-white hover:shadow-[0_30px_70px_-25px_rgba(217,27,92,0.7)]'
                        : 'border border-navy/10 bg-white hover:border-crimson/40 hover:shadow-[0_24px_60px_-20px_rgba(11,19,43,0.25)]'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className={`font-display font-extrabold ${i === 0 ? 'text-6xl text-white/25 lg:text-8xl' : 'text-4xl text-navy/15'}`}>
                        {s.id}
                      </span>
                      <ArrowUpRight className={`h-5 w-5 transition-transform duration-300 group-hover:rotate-45 ${i === 0 ? 'text-gold' : 'text-crimson'}`} />
                    </div>
                    <div className={i === 0 ? 'mt-24' : 'mt-10'}>
                      <span className={`font-mono text-[10px] font-bold uppercase tracking-[0.25em] ${i === 0 ? 'text-gold' : 'text-crimson'}`}>
                        {s.category}
                      </span>
                      <h3 className={`mt-3 font-display font-extrabold leading-tight tracking-tight ${i === 0 ? 'text-2xl lg:text-3xl' : 'text-lg'}`}>
                        {s.title}
                      </h3>
                    </div>
                  </Link>
                </FadeUp>
              );
            })}
            <FadeUp delay={0.35}>
              <Link
                to="/seminarios"
                data-testid="seminars-view-all"
                className="group flex h-full min-h-[180px] flex-col items-start justify-between rounded-2xl bg-navy p-6 text-white transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_30px_70px_-25px_rgba(11,19,43,0.8)] sm:p-8"
              >
                <span className="font-display text-4xl font-extrabold text-white/20">08</span>
                <div>
                  <h3 className="font-display text-lg font-extrabold leading-tight">Ver los 8 seminarios</h3>
                  <span className="mt-3 inline-flex items-center gap-2 font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-gold">
                    Explorar <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ============ INCLUYE ============ */}
      <section data-testid="includes-section" className="noise relative overflow-hidden bg-navy-deep py-16 sm:py-24 lg:py-32">
        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <FadeUp>
              <Overline className="text-gold">Compromiso con la excelencia</Overline>
            </FadeUp>
            <MaskedLines
              inView
              className="mt-6 font-display text-2xl font-extrabold uppercase leading-tight tracking-tight sm:text-3xl lg:text-4xl"
              lines={[<>Un entrenamiento intensivo,</>, <>bases <span className="text-crimson">tácticas</span> reales</>]}
            />
            <FadeUp delay={0.15}>
              <p className="mt-6 text-base leading-relaxed text-white/60">
                Diseñado para instalar las bases tácticas que todo joven necesita para destacar en el
                bachillerato y asegurar su éxito universitario, mediante competencias prácticas para el mundo real.
              </p>
            </FadeUp>
          </div>

          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4 lg:gap-8">
            {INCLUDES.map((item, i) => (
              <FadeUp key={item.title} delay={0.08 * i}>
                <article
                  data-testid={`include-card-${i + 1}`}
                  className="group flex h-full flex-col rounded-2xl border border-white/10 bg-navy-card/60 p-6 backdrop-blur transition-all duration-500 hover:-translate-y-1.5 hover:border-crimson/50"
                >
                  <div className="flex h-44 items-center justify-center overflow-hidden">
                    <img
                      src={item.img}
                      alt={item.title}
                      className="max-h-full w-auto object-contain mix-blend-screen transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <div className="mt-6 border-t border-white/10 pt-5">
                    <span className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-gold">Incluye 0{i + 1}</span>
                    <h3 className="mt-2 font-display text-lg font-bold leading-snug">{item.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-white/55">{item.desc}</p>
                  </div>
                </article>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ============ CIERRE ============ */}
      <section data-testid="closing-section" className="noise relative overflow-hidden bg-crimson py-16 text-white sm:py-24 lg:py-32">
        <div className="relative z-10 mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-6 lg:grid-cols-12 lg:px-8">
          <div className="lg:col-span-6">
            <FadeUp>
              <Overline className="text-gold">El primer paso</Overline>
            </FadeUp>
            <MaskedLines
              inView
              className="mt-6 font-serif text-4xl italic leading-[1.05] sm:text-5xl lg:text-6xl"
              lines={[<>Asegure su</>, <>ventaja</>, <>competitiva</>]}
            />
            <FadeUp delay={0.2}>
              <p className="mt-8 max-w-lg text-base leading-relaxed text-white/85 sm:text-lg">
                El lugar de su hijo en Future Leaders es el primer paso para construir el expediente
                académico que las mejores universidades exigen. Prepárelo hoy para los retos del mañana.
              </p>
            </FadeUp>
            <FadeUp delay={0.3}>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link to="/seminarios" data-testid="closing-cta-seminarios" className="btn-light group">
                  Conocer el programa
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
                <Link to="/aval-internacional" data-testid="closing-cta-aval" className="btn-ghost border-white/40">
                  Ver el aval
                </Link>
              </div>
            </FadeUp>
            <FadeUp delay={0.4}>
              <div className="mt-12 flex flex-wrap gap-x-8 gap-y-3">
                {['Certificado internacional', 'Mentorías en vivo', 'Aval de EE.UU.'].map((t) => (
                  <span key={t} className="inline-flex items-center gap-2 text-sm text-white/80">
                    <BadgeCheck className="h-4 w-4 text-gold" /> {t}
                  </span>
                ))}
              </div>
            </FadeUp>
          </div>
          <FadeUp delay={0.2} className="lg:col-span-6">
            <div className="relative">
              <div className="overflow-hidden rounded-t-[180px] border border-white/30 p-3">
                <img
                  src={IMAGES.teensTrio}
                  alt="Tres jóvenes estudiantes con útiles escolares"
                  className="h-[380px] w-full rounded-t-[168px] bg-navy object-cover sm:h-[460px] max-h-[80vh]"
                  data-testid="closing-image"
                />
              </div>
              <div className="absolute -right-4 top-8 flex items-center gap-2 rounded-full bg-navy px-5 py-3 shadow-xl sm:-right-8">
                <Globe2 className="h-4 w-4 text-gold" />
                <span className="font-mono text-[10px] font-bold uppercase tracking-[0.2em]">Validez global</span>
              </div>
              <div className="absolute -left-4 bottom-10 flex items-center gap-2 rounded-full bg-gold px-5 py-3 text-navy shadow-xl sm:-left-8">
                <ShieldCheck className="h-4 w-4" />
                <span className="font-mono text-[10px] font-bold uppercase tracking-[0.2em]">Excelencia académica</span>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
